import { apiClient as api } from "./httpClient";

/** Linha do Livro-caixa. Traz os ids junto dos nomes: a mesma resposta alimenta a lista e o formulário. */
export interface AccountPayableItem {
  id: string;
  dateToPay: string;
  name: string;
  notes: string;
  operationType: string;
  predictedValue: number | null;
  definitiveValue: number | null;
  paymentStatus: boolean;
  paymentDate: string | null;
  categoryId: string | null;
  categoryName: string;
  subCategoryId: string | null;
  subCategoryName: string;
  groupName: string;
  paymentOriginId: string | null;
  accountName: string;
  notConsiderInDre: boolean;
  /** quitação: a provisão que ela quita (item 138) */
  provisionId: string | null;
  provisionDateToPay: string | null;
  provisionName: string | null;
  closesProvision: boolean;
  /** provisão: soma das quitações ligadas e se já fechou */
  settledTotal: number;
  provisionClosed: boolean;
  recurrenceGroupId: string | null;
  /** marca de provisão: quitada por outro lançamento; sem ela, baixa na própria linha */
  isProvision: boolean;
}

/** Provisão em aberto para o passo 2 (quitar): o que ainda falta dela. */
export interface OpenProvision {
  id: string;
  dateToPay: string;
  name: string;
  operationType: string;
  categoryId: string | null;
  categoryName: string;
  subCategoryId: string | null;
  subCategoryName: string;
  predictedValue: number;
  settledTotal: number;
  remaining: number;
  /** conta prevista na provisão (pré-seleciona o passo 2) */
  paymentOriginId: string | null;
  /** true = provisão (quitação em lançamento novo); false = lançamento comum (baixa na própria linha) */
  isProvision: boolean;
}

/**
 * Previsão e realizado são leituras diferentes do mesmo período: a previsão soma o valor previsto
 * de tudo; o realizado soma o valor efetivo só de quem teve baixa.
 */
export interface AccountPayablePage {
  items: AccountPayableItem[];
  total: number;
  forecastInflow: number;
  forecastOutflow: number;
  forecastInflowCount: number;
  forecastOutflowCount: number;
  realizedInflow: number;
  realizedOutflow: number;
  realizedInflowCount: number;
  realizedOutflowCount: number;
  /**
   * O que ESTA nos totais mas nao e resultado: transferencia entre contas do mesmo titular e
   * marcador de saldo. Sem isso, quem compara com o Fluxo de Caixa acha que uma tela erra.
   */
  outOfResult: CashBookSlice[];
}

export interface PaymentOriginOption {
  id: string;
  name: string;
}

/** Todos opcionais e combináveis. `settlement`: "settled", "pending" ou vazio para os dois. */
export interface AccountPayableFilters {
  search?: string;
  categoryId?: string;
  subCategoryId?: string;
  operation?: string;
  paymentOriginId?: string;
  settlement?: string;
  /** "provision" | "regular"; vazio = todos */
  kind?: string;
}

export interface AccountPayablePayload {
  name: string;
  description?: string | null;
  dateToPay: string;
  predictedValue: number;
  predictedValueWithoutSurcharge?: number | null;
  definitiveValue?: number | null;
  definitiveValueWithoutSurcharge?: number | null;
  paymentDate?: string | null;
  paymentStatus?: boolean;
  accountPayableTypeId?: string | null;
  accountPayableSubTypeId?: string | null;
  paymentOriginId?: string | null;
  operationType?: string | null;
  /** repetições (o "Rec. MENSAL" do sistema antigo, máx. 60); `recurrenceUnit` month|week */
  recurrence?: number | null;
  recurrenceUnit?: string | null;
  /** quitação de uma provisão (item 138): o lançamento nasce pago e ligado a ela */
  provisionId?: string | null;
  closesProvision?: boolean | null;
  isProvision?: boolean;
  notConsiderInDRE?: boolean;
  comments?: string | null;
  entity?: string | null;
}

export const accountsPayableService = {
  list: async (
    from: string,
    to: string,
    page: number,
    pageSize: number,
    filters?: AccountPayableFilters,
  ): Promise<AccountPayablePage> => {
    const response = await api.get("/accounts-payable", {
      params: {
        from,
        to,
        page,
        pageSize,
        ...(filters?.search ? { search: filters.search } : {}),
        ...(filters?.categoryId ? { categoryId: filters.categoryId } : {}),
        ...(filters?.subCategoryId ? { subCategoryId: filters.subCategoryId } : {}),
        ...(filters?.operation ? { operation: filters.operation } : {}),
        ...(filters?.paymentOriginId ? { paymentOriginId: filters.paymentOriginId } : {}),
        ...(filters?.settlement ? { settlement: filters.settlement } : {}),
        ...(filters?.kind ? { kind: filters.kind } : {}),
      },
    });
    return response.data;
  },

  paymentOrigins: async (): Promise<PaymentOriginOption[]> => {
    const response = await api.get("/accounts-payable/payment-origins");
    return response.data;
  },

  /** Planilha do período, no mesmo recorte dos filtros (sem paginação). */
  /** Saldo por conta no recorte do filtro (RN-208b): soma bate com o Saldo do Realizado quando o período é o mês desde o dia 1. */
  balances: async (from: string, to: string): Promise<CashBookSlice[]> => {
    const response = await api.get("/accounts-payable/balances", { params: { from, to } });
    return response.data;
  },

  exportXlsx: async (
    from: string,
    to: string,
    filters?: AccountPayableFilters,
  ): Promise<Blob> => {
    const response = await api.get("/accounts-payable/export", {
      params: {
        from,
        to,
        ...(filters?.search ? { search: filters.search } : {}),
        ...(filters?.categoryId ? { categoryId: filters.categoryId } : {}),
        ...(filters?.subCategoryId ? { subCategoryId: filters.subCategoryId } : {}),
        ...(filters?.operation ? { operation: filters.operation } : {}),
        ...(filters?.paymentOriginId ? { paymentOriginId: filters.paymentOriginId } : {}),
        ...(filters?.settlement ? { settlement: filters.settlement } : {}),
        ...(filters?.kind ? { kind: filters.kind } : {}),
      },
      responseType: "blob",
    });
    return response.data;
  },


  categories: async (): Promise<AccountCategoryOption[]> => {
    const response = await api.get("/accounts-payable/categories");
    return response.data;
  },

  create: async (payload: AccountPayablePayload): Promise<string> => {
    const response = await api.post("/accounts-payable", payload);
    return response.data;
  },

  /** Provisões em aberto para o passo 2; `operationType` filtra Débito/Crédito. */
  openProvisions: async (from: string, to: string, operationType?: string): Promise<OpenProvision[]> => {
    const response = await api.get("/accounts-payable/open-provisions", {
      params: { from, to, ...(operationType ? { operationType } : {}) },
    });
    return response.data;
  },

  update: async (id: string, payload: AccountPayablePayload): Promise<void> => {
    await api.put(`/accounts-payable/${id}`, payload);
  },

  /** Baixa do lançamento. `settled: false` desfaz e devolve à condição de previsto. */
  settle: async (
    id: string,
    payload: {
      paymentDate?: string | null;
      definitiveValue?: number | null;
      definitiveValueWithoutSurcharge?: number | null;
      settled?: boolean;
      paymentOriginId?: string | null;
      comments?: string | null;
    },
  ): Promise<void> => {
    await api.post(`/accounts-payable/${id}/settle`, payload);
  },

  /** Exclusão lógica; a justificativa é obrigatória e fica na observação do lançamento. */
  remove: async (id: string, reason: string): Promise<void> => {
    await api.delete(`/accounts-payable/${id}`, { params: { reason } });
  },
};

/** Categoria do lançamento. `parentCategoryId` nulo = categoria; preenchido = subcategoria dela. */
/** Uma fatia do painel: categoria ou conta, com quantidade e valor. */
export interface CashBookSlice {
  label: string;
  count: number;
  value: number;
}

export interface AccountCategoryOption {
  id: string;
  name: string;
  parentCategoryId: string | null;
  groupName: string;
}
